import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';

const Methodology = () => {
  const [openPanels, setOpenPanels] = useState({});

  const togglePanel = (panelId) => {
    setOpenPanels(prev => ({
      ...prev,
      [panelId]: !prev[panelId]
    }));
  };

  const formatContent = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let i = 0;
    let key = 0;

    const textClass = 'text-gray-700 dark:text-gray-300 text-sm leading-relaxed';
    const linkClass = 'text-blue-600 dark:text-blue-400 hover:underline break-all';

    const renderUrl = (str) => {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = str.split(urlRegex);
      return parts.map((part, idx) =>
        part.match(urlRegex) ? (
          <a key={idx} href={part} target="_blank" rel="noopener noreferrer" className={linkClass}>
            {part}
          </a>
        ) : (
          part
        )
      );
    };

    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();
      const indent = line.match(/^(\s*)/)?.[1]?.length || 0;

      if (!trimmed) {
        i++;
        continue;
      }

      // Numbered section header (e.g., "1. Import Dependency", "2.1 Supply Risk")
      if (/^\d+\.(\d+\.)?\s+.+/.test(trimmed)) {
        elements.push(
          <h3 key={key++} className="font-semibold text-gray-900 dark:text-white mt-8 mb-3 text-base first:mt-0 pt-2 first:pt-0 border-t border-gray-100 dark:border-gray-700 first:border-t-0">
            {trimmed}
          </h3>
        );
        i++;
        continue;
      }

      // Sub-header with colon (e.g., "Formula:", "Interpretation:", "Data sources:")
      if (/^[A-Za-z][A-Za-z\s]+:$/.test(trimmed) && trimmed.length < 50) {
        const label = trimmed.replace(':', '');
        elements.push(
          <h4 key={key++} className="font-medium text-gray-900 dark:text-white mt-5 mb-2 text-sm">
            {label}
          </h4>
        );
        i++;
        continue;
      }

      // Standalone section labels (e.g., "Purpose", "Formula", "Components", "Logic")
      const sectionLabels = ['Purpose', 'Formula', 'Logic', 'Components', 'Data Inputs', 'Interpretation', 'Visualization Logic', 'Data Processing Steps', 'Scoring Thresholds', 'Scoring Logic'];
      if (sectionLabels.includes(trimmed) && i + 1 < lines.length && lines[i + 1].trim() && !lines[i + 1].trim().startsWith('-')) {
        elements.push(
          <h4 key={key++} className="font-medium text-gray-900 dark:text-white mt-5 mb-2 text-sm">
            {trimmed}
          </h4>
        );
        i++;
        continue;
      }

      // Bullet point (starts with -)
      if (trimmed.startsWith('- ')) {
        const bullets = [];
        while (i < lines.length) {
          const bl = lines[i].trim();
          if (bl.startsWith('- ')) {
            bullets.push(bl.slice(2));
            i++;
          } else if (bl === '' || /^\d+\.(\d+\.)?\s/.test(bl) || /^[A-Za-z][A-Za-z\s]+:$/.test(bl)) {
            break;
          } else {
            bullets.push(bl);
            i++;
          }
        }
        elements.push(
          <ul key={key++} className="list-disc list-outside ml-5 mb-5 space-y-2">
            {bullets.map((b, bi) => (
              <li key={bi} className={textClass}>
                {renderUrl(b)}
              </li>
            ))}
          </ul>
        );
        continue;
      }

      // Tab-indented list items (sub-bullets)
      if (indent >= 1 && !trimmed.startsWith('-') && trimmed.length < 120) {
        const items = [];
        while (i < lines.length) {
          const ll = lines[i];
          const t = ll.trim();
          const ind = ll.match(/^(\s*)/)?.[1]?.length || 0;
          if (ind >= 1 && t && !t.startsWith('-') && !/^\d+\.(\d+\.)?\s/.test(t) && !/^[A-Za-z][A-Za-z\s]+:$/.test(t)) {
            items.push(t);
            i++;
          } else if (t === '') {
            i++;
          } else {
            break;
          }
        }
        if (items.length > 0) {
          elements.push(
            <ul key={key++} className="list-disc list-outside ml-5 mb-5 space-y-2">
              {items.map((item, ii) => (
                <li key={ii} className={textClass}>
                  {renderUrl(item)}
                </li>
              ))}
            </ul>
          );
        }
        continue;
      }

      // Simple table (tab-separated like "Import Dependency	Score")
      const nextLines = [];
      let j = i;
      while (j < lines.length) {
        const nl = lines[j].trim();
        if (nl && nl.includes('\t') && nl.split('\t').length >= 2 && !nl.startsWith('-')) {
          nextLines.push(nl);
          j++;
        } else if (nl === '') {
          j++;
        } else {
          break;
        }
      }
      if (nextLines.length >= 2) {
        const headers = nextLines[0].split('\t');
        const rows = nextLines.slice(1).map(r => r.split('\t'));
        elements.push(
          <div key={key++} className="my-5 overflow-x-auto rounded border border-gray-200 dark:border-gray-600">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800">
                  {headers.map((h, hi) => (
                    <th key={hi} className="px-4 py-2 text-left font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600">
                      {h.trim()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                    {row.map((cell, ci) => (
                      <td key={ci} className={`px-4 py-2 ${textClass}`}>
                        {cell.trim()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        i = j;
        continue;
      }

      // Formula line (contains = and looks like formula)
      if (trimmed.includes('=') && (trimmed.includes('"') || trimmed.includes('×') || trimmed.includes('∑') || trimmed.includes('/') || trimmed.includes('('))) {
        elements.push(
          <div key={key++} className="my-5 px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded border-l-4 border-blue-500 font-mono text-sm overflow-x-auto">
            {trimmed}
          </div>
        );
        i++;
        continue;
      }

      // Where / Note type lines
      if (trimmed.startsWith('Where:') || trimmed.startsWith('Note:') || trimmed.startsWith('Purpose:')) {
        elements.push(
          <p key={key++} className={`${textClass} mb-4 italic`}>
            {renderUrl(trimmed)}
          </p>
        );
        i++;
        continue;
      }

      // Regular paragraph
      const paraLines = [trimmed];
      i++;
      while (i < lines.length) {
        const next = lines[i];
        const nextTrimmed = next.trim();
        if (nextTrimmed === '' || nextTrimmed.startsWith('- ') || /^\d+\.(\d+\.)?\s/.test(nextTrimmed) ||
            /^[A-Za-z][A-Za-z\s]+:$/.test(nextTrimmed) || next.includes('\t')) {
          break;
        }
        paraLines.push(nextTrimmed);
        i++;
      }
      elements.push(
        <p key={key++} className={`${textClass} mb-5`}>
          {renderUrl(paraLines.join(' '))}
        </p>
      );
    }

    return <div className="space-y-4">{elements}</div>;
  };

  const methodologySections = [
    {
      id: 'overview-data',
      title: 'Overview',
      content: `
1. Import Dependency Calculation
Import dependency measures how reliant the country is on foreign sources for a mineral.

Formula:
"Import Dependency (%)" = ("Imports" -"Exports" )/"Domestic Consumption" ×100
Where: "Domestic Consumption"="Production"+"Imports"-"Exports" 

Interpretation:
- >100% = Fully import dependent + stockpiling/demand growth
- 0–100% = Partial import dependency
- <0% = Net exporter (rare for critical minerals)


2. Import Growth Calculation
Measures year-over-year change in import volume/value.
"Import Growth (%)"=(("Imports")_t - ("Imports")_(t-1)) / ("Imports")_(t-1) ×100


3. Price Trend Calculation - Uses annual average price across the last 12 months.
"Price Trend (%)" = (("Avg Price")_t -("Avg Price")_(t-1)) /("Avg Price")_(t-1) ×100


4. Supplier Share (%)
Based on the share of import value by partner country. This is used later in the concentration and political risk scoring.
"Country Share (%)"="Imports from Country" /"Total Imports" ×100


5. Domestic Reserve & Production Indicators - Used to evaluate long-term self-reliance potential.
- Reserves: USGS / national geological data
- Production: Indian Minerals Yearbook 2023 



2. Multi-Dimensional Risk Assessment (Single Mineral) — Methodology
The radar chart scores four risk pillars on a 1–5 scale, where:
	1 = Low risk
	5 = Critical risk

  2.1 Supply Risk Score
Raw Score = Import Dependency % × (1 + Top Supplier Share / 100)
Supply Risk = 1 + (Raw Score / 200) × 4

2.2 Price Risk Score
Average Price = Sum of 12 monthly prices ÷ 12
Standard Deviation = Calculate variation from average
Volatility % = (Standard Deviation ÷ Average) × 100

Price Risk Mapping:
- Volatility < 10% → Score = 1
- Volatility 10-20% → Score = 2
- Volatility 20-30% → Score = 3
- Volatility 30-50% → Score = 4
- Volatility > 50% → Score = 5

2.3 Political Risk Score
Weighted PRI = Σ(Supplier Share % × Supplier PRI) / 100
Political Risk = Weighted PRI (already on 1-5 scale)

1.0-2.0 = Stable democracies, low political risk
2.1-3.0 = Mixed suppliers, moderate risk
3.1-4.0 = Some unstable suppliers, high risk
4.1-5.0 = Mostly risky countries, critical risk

2.4 Concentration Risk Score
HHI = Σ(Market Share %)²

Concentration Risk Mapping:
- HHI < 1000 → Score = 1 (many small suppliers)
- HHI 1000-1800 → Score = 2 (moderate concentration)
- HHI 1800-2500 → Score = 3 (medium concentration)
- HHI 2500-4000 → Score = 4 (high concentration)
- HHI > 4000 → Score = 5 (very high concentration)

2.5 Overall Risk Score
Overall = (Supply + Price + Political + Concentration) / 4


2.1 Supply Risk Score
Components
- Import dependency (%)
- Dominant supplier share (%)

Formula
"Supply Risk Score" = 0.6 "Import Dependency Score"+ 0.4 "Top Supplier Concentration Score"

Scoring Logic (normalized 1–5)
Import Dependency	Score
< 30%	1 (Low)
30–60%	2
60–90%	3
90–120%	4
>120%	5 (Critical)


2.2 Price Risk Score
Based on 12-month price volatility.
Formula
"Price Volatility"=σ_"monthly prices" / μ_"annual price"  

Scoring Thresholds
Volatility	Score
<10%	1
10–20%	2
20–35%	3
35–50%	4
>50%	5


2.3 Political Risk Score
Derived from top supplier country political indices, weighted by import share.
Formula
"Political Risk Score"=∑("Country Political Risk Index"×"Import Share")


Data sources:
- FSI Scores - https://fragilestatesindex.org/excel/
- WGI Scores - https://databank.worldbank.org/source/worldwide-governance-indicators
- Scores converted to 1–5 scale.


2.4 Concentration Risk Score
Based on market concentration of suppliers.
Formula (Herfindahl–Hirschman Index)
HHI=∑(s_i^2)
"Concentration Score"={
- 1 &HHI <1500
- 2 1500 - 2500
- 3 2500 - 3500
- 4 3500 - 4500
- 5 >4500
    }

Where s_i= country share (%)

2.5 Composite Risk Score (Shown in the Chart)

"Composite Score"=("Supply"+"Price" +"Political" +"Concentration" ) / 4

This is the value displayed next to each mineral in the All-Minerals Radar chart.


3. Multi-Mineral Comparative Radar (Overlay) — Methodology
This visualization compares all minerals on the same 1–5 scale.
Each mineral’s four risk scores are plotted on the same axes:
- Supply
- Price
- Political
- Concentration


Why Overlay?
Overlay allows identification of:
- Highest-risk minerals
- Which dimension drives the risk
- Similarity patterns between mineral risk profiles
- Prioritization for policy intervention
- Minerals requiring urgent diversification


4. Data Sources Used Across All Metrics 
- UNComtrade - https://comtradeplus.un.org/TradeFlow
- Trading Economics - https://tradingeconomics.com/commodity/
- FSI Scores - https://fragilestatesindex.org/excel/
- WGI Scores - https://databank.worldbank.org/source/worldwide-governance-indicators


Final Executive Summary of Methodology
- Import dependency quantifies strategic exposure.
- Supplier shares quantify geoeconomic concentration risk.
- Price volatility quantifies market instability.
- Political risk quantifies exposure to unstable partners.
- Composite risk combines all four into a single actionable score.
- Multi-year comparisons identify trend shifts and emerging vulnerabilities.
- Radar overlays show cross-mineral prioritization for policymakers.

`},
{
      id: 'india-trade-pricing-data',
      title: 'Trade and Pricing Data',
      content: `This dashboard uses standardized analytical methods to track, compare, and interpret global trade flows and commodity price movements for critical minerals. All indicators are designed to simplify complex data into actionable insights.
      
      1. Trade Analysis Methodology
      
      1.1 Data Sources
      Trade data is aggregated from monthly import records, customs databases, and partner-country trade statistics. Values are standardized in USD. 
      Trade Data - https://comtradeplus.un.org/TradeFlow
      Pricing Data - https://tradingeconomics.com/commodity/


      1.2 Rolling Averages (3-Month, 6-Month, 12-Month)
      Rolling averages smooth short-term volatility to reveal underlying trade trends.
      3-Month Average (3M): Captures short-term momentum and recent fluctuations.
	    6-Month Average (6M): Balances medium-term stability with moderate responsiveness.
	    12-Month Average (12M): Shows long-term structural direction, reducing seasonal noise.

      Formula: 
      ("Rolling Average")_n = ("Sum of last " n" months" )/n


      1.3 Monthly Growth Rates (%) - Measures how imports changed from the previous month.

      "Monthly Growth (%)"=(("Current Month" -"Previous Month" ))/"Previous Month" ×100

      Used to detect short-term surges, slowdowns, or shocks.


1.4 Year-over-Year Growth (%) - Compares each month to the same month in the previous year to remove seasonality.

"YoY Growth (%)"=(("Month")_t-("Month")_(t-1)))/ ("Month")_(t-1) × 100

Tracks structural changes in trade volumes.



1.5 Partner Country Concentration
Imports are grouped by top supplier countries each month and across years to identify:
1. Dependency risks
2. Shifts in sourcing
3. Geopolitical exposure

Stacked bars and long-term comparisons reveal how partner shares evolve.



1.6 Multi-Year Period Comparison
Compares total imports over the latest 3-year or 5-year period against the preceding periods.
Used for understanding long-horizon changes in demand and sourcing stability.


2. Pricing Analysis Methodology
2.1 Commodity Price Data
Price data (USD per tonne) is collected from global commodity exchanges, benchmark indices, and industry pricing services.


2.2 Monthly Price Levels
Raw monthly average prices are plotted to show baseline price movement and direction.


2.3 Monthly Price Growth (%)
Shows short-term pricing volatility.

Formula:
"Monthly Price Growth (%)"=(("Current Price" -"Previous Price" ))/"Previous Price"×100


2.4 Year-over-Year Price Growth (%)
Captures structural price changes and long-term cost pressure trends.

Formula:
"YoY Price Growth (%)"=(("Price")_t -("Price")_(t-1)))/("Price")_(t-1)×100



2.5 Price Momentum (Rolling 3M, 6M, 12M)
Rolling averages are applied to pricing data to identify underlying cycles such as:
1. Market cooling
2. Price corrections
3. Sustained uptrends
Helps distinguish temporary volatility from long-term movement.


2.6 Price Volatility (σ)
Volatility is measured as the standard deviation of price changes over time.
σ=√((∑(x-x ˉ)^2)/N)

Used to flag minerals with unstable or risky pricing environments.


2.7 Largest Declines, Sharpest Movements
Summary metrics identify significant pricing events:
1. Steepest month-to-month drop
2. Highest positive movement
3. Biggest YoY correction

Helps detect supply-demand shocks early.


3. Integration for Geoeconomic Intelligence
The trade and pricing methodologies together enable:
- Detection of early supply-chain risks
- Identification of geopolitical dependencies
-	Understanding of structural market shifts
- Forecasting of procurement exposure
- Cross-mineral comparison for risk management

This combined analytical framework forms the backbone of the geoeconomic intelligence dashboard.


    `},
    {
      id: 'resource-distribution',
      title: 'Resource Distribution (India)',
      content: `
Purpose
The Resource Distribution module maps domestic reserves and production potential of critical minerals across Indian states.
It supports national-level planning for:
- Exploration & mining strategy
- Domestic capacity development
- Reducing import dependence
- Identifying high-potential mineral clusters


1. Data Inputs

1.1 Mineral Reserves (tonnes)
- State-level reserve estimates published by Indian Bureau of Mines (IBM)
- Mineral-wise reserve data for: Cobalt, Graphite, Nickel

1.2 Mineral Production - Annual state production for each mineral (where available)

1.3 State Boundary Shapes
- GIS boundary files for all Indian states and UTs
- Used to map reserve quantities spatially

1.4 Derived Indicators
Top Reserve State
Highest share of total Indian reserves
	Most Concentrated Mineral
Minerals with >60–80% reserves concentrated in ≤2 states
	Total National Reserves
Sum of reserves across all states for selected mineral 



2. Data Processing Steps
2.1 Reserve Normalization
Reserves for each state are converted into percentage share:
"State Share (%)"="State Reserves" /"Total India Reserves" ×100

Used for:
	Ranking states
	Identifying concentration hotspots


2.2 Concentration Index (HHI-style)
A modified concentration score is calculated:
"Concentration Score"=∑_(i=1)^n(s_i^2)

Where s_i= share of state i in total reserves
Used to assess:
	Whether a mineral is geographically diversified
	Or highly concentrated (risk of supply bottlenecks within India)



2.3 Bubble Map Sizing
Circle size reflects the log-scaled reserve quantity for visual clarity:
"Bubble Radius"∝log⁡("Reserves")

Ensures:
	Very large states don’t overwhelm the map
	Small but important reserves remain visible



2.4 State Ranking
States are automatically classified into:
	High Reserve Zone (>20% share)
	Medium Reserve Zone (5–20%)
	Low Reserve Zone (<5%)
Displayed through varying circle sizes or color intensities. 



3. Visualization Logic
3.1 Map Interface
	India outline map
	Bubble overlays represent reserve size
	Hover tooltip shows: 
	Reserve quantity
	Share of national total
	Mineral type
3.2 Summary Panels
Each visualization contains:
	Top Reserve State 
	Most Concentrated Mineral 
	Total Indian Reserves across selected mineral



4. Interpretation for India
This section helps answer strategically important questions:
4.1 Domestic Capability
	Which states have the strongest mining potential?
	Are reserves geographically concentrated or diversified?
4.2 Strategic Planning
	Where should India invest in:
	extraction
	refining
	infrastructure
	public-private mining partnerships
4.3 Import Reduction Roadmap
Identifies minerals where India can reduce import ratio because of available reserves.

`
    },

{
      id: 'global-supply-import-dependence-trade-restrictions-data',
      title: 'Global Supply, Import Ratio, Trade Restrictions',
      content: `
      Methodology for Global Supply, Import Ratio & Trade Restriction Maps
(India-Centric Framework)
This section outlines how each map and indicator is calculated. All metrics are computed from India’s perspective — India’s imports, India’s risks, and India’s global exposure.


1. Import Ratio Methodology
Purpose:
Measures how dependent India is on each country for a specific mineral.
Formula: 
"Import Ratio (%)"=("India’s Imports from Country X" /"India’s Total Imports of the Mineral" )×100

Data Inputs:
	India’s annual import value (USD) or weight (tonnes)
	Import value by partner country
	Mineral selected (e.g., lithium, cobalt, REE)
	Selected year
How It Is Displayed on the Map:
	Darker shade = Higher dependence
	Countries with 0 imports remain white
	Tooltip shows:
	Import Ratio for the selected country
	Alerts (if any)
	Partnership / KABIL status 
Interpretation for India:
Higher ratio → higher dependence → higher supply-chain vulnerability.



2. Country Detail Pop-Up Methodology 
Purpose:
Provides detailed risk information when a country is selected.
Data Included:
	Import Ratio %
	Alerts & geopolitical events (if available)
	KABIL agreement information
	Notes on supply disruptions (if detected)
Data Sources: 
Logic:
	Displays only India-specific information
	Alerts triggered when a country has:
	Export restrictions
	Political instability
	Major supply chain events
	New agreements or cancelled MoUs



3. World Reserves Methodology
Purpose:
Shows which countries hold the world’s known reserves of critical minerals India needs.
Data Inputs:
	Total world reserves (tonnes)
	Reserve estimates by country
	Mineral selected
	Year (latest available)
Visualization Logic:
	Circle size = reserve volume
	Largest circles = most resource-abundant countries
	Tooltip shows: 
	Reserve tonnage
	Top reserve holder globally
Interpretation for India:
Countries with large reserves = priority partners for long-term supply agreements (KABIL, G2G, JVs).



4. Trade Restrictions Methodology
Purpose:
Identifies policy-related risks affecting India’s ability to import minerals.
Restriction Categories:
Color	Category	Meaning
🔴 Red	Export Prohibition	Mineral cannot be exported
🟠 Orange	Export Quota	Restricted export quantity
🟡 Yellow	Export Tax	Exporters pay tax → cost rises
🟢 Green	Licensing Requirement	Export requires government approval
🟣 Pink	Other Measures	Indirect controls, value-add rules
⚪ White	No Restrictions	Free export
⚫ Grey	Not Researched	No data available
Data Sources:
	International Energy Agency
	Global Trade Alert
	Government export policies
	Public policy announcements
	News pipelines
Logic:
Each country is tagged based on official export rules for the selected mineral and year.
Interpretation for India:
Shows where regulatory barriers may impact India’s supply stability or cost.



5. Trade Policies
Purpose
The Trade Policy Tracker compiles, classifies, and displays all trade policies related to a selected mineral, across all countries.
This includes:
	Export bans, quotas, taxes
	Licensing rules
	Strategic partnerships
	Bilateral agreements
	Critical mineral value-chain measures
	National or regional policy announcements
It provides a global view of how policy changes shape mineral supply chains.



Data Inputs
For every policy detected globally:
	Policy Title
	Country (Implementing Country)
	Affected Mineral (e.g., Lithium, Cobalt, REE)
	Policy Type
	Export prohibition
	Export quota
	Export tax
	Licensing requirement
	Other trade measures
	Strategic partnership / MoU
	Status:
	Announced
	Enacted
	Amended
	Repealed
	Year of Policy
	Jurisdiction:
	National
	Regional (e.g., EU)
	Bilateral
	Official Source: 
	IEA Critical Minerals Policy Tracker
	WTO notifications
	Government release
	Trade ministry press notes
	Verified news wires


Data Processing Steps
1. Policy Extraction
Policies related to the selected mineral are extracted from global datasets and news sources.
2. Classification
Each policy is tagged into a standardized category:
	Export Ban
	Export Quota
	Export Tax
	Licensing Requirement
	Other Restrictive Measures
	Strategic Partnership / MoU
	No Restriction (explicit declarations)
3. Filtering & Deduplication
	Duplicate entries across sources are merged
	Outdated policies are archived
	Amendments are linked to original policies
	Only mineral-relevant content is retained
4. Standardization
Policy descriptions are converted into short, structured summaries:
	Country → Mineral → Policy Type → Impact
5. Mapping to the Dashboard
	Policies appear on the map as color-coded markers
	Policy list appears under “Trade Policy Tracker”
	Clicking a country reveals all policies associated with that country


6. KPI Cards: Import Ratio + World Value Methodology
Purpose:
Summaries displayed below the maps.
Metrics:
	World Value
"Total global trade in selected mineral for the year"

	Top Importer for India
	Country with highest Import Ratio for India
	Top Reserve Holder
	Country with largest reserves globally
Interpretation for India:
Helps quickly identify India’s key suppliers and global supply hotspots.


7. Map Color Scaling & Classification
Technique:
	Maps use a quantile scale or min-max normalization
	Ensures meaningful visual contrast
	High-dependence countries appear dark
	Low-dependence countries appear light
Why This Matters:
Improves interpretability for policymakers reviewing India’s import risks.
`
    },
    
    {
      id: 'import-export-dependency',
      title: 'Import/Export Dependency Scores',
      content: `India's Perspective all partners (India Trade Yearly Data) - notes

This note talks about what all processes have taken place to get the final results in this section of the data. 

DESCRIPTION:

This data tells us about the import dependencies of India on different countries for the 5 critical minerals (lithium, graphite, nickel, rare earth elements, cobalt) over the years (2020, 2021, 2022, 2023, 2024) based on the import data that has been collected. 

SOURCE:

UN Comtrade
https://comtradeplus.un.org/TradeFlow

PROCESS:

The data was first downloaded from un comtrade 
The required columns were selected to have a cleaner dataset that is easier to read. 
TradeData is the original file that was downloaded, as the versions changed the file name changed along with it 
A new column named mineral is added and here based on all the cmdcodes that come under a certain mineral a broad mineral name has been given to get the aggregates 
It is made sure that the unit for both qty and primaryValue is both uniform throughout
the unit for qty is kg and for primaryValue it is USD($)
We filter out the partner given as India, these values are noted and so is the period and cmd code
these values, both qty and primaryValue are subtracted from the world totals to remove redundancies as India cannot be India's trade partner. 
once the values are subtracted from the world totals these values are deleted from the data and the blank rows are removed
The final raw data that has been used to run the code on for the results is named TradeData_indiavfinalvcsv
once we get the final dataset, the code is run, the code needs to be changed based on the location the file is in on the laptop
once the code is run the final results that we get would be named mineral_level_country_dependency, and can be renamed based on choice 


CALCULATIONS DONE:

Subtracting India value and quantity from world totals to remove redundancies 
import dependency ratio: (primaryValue of partner country)/(mineral_world_import_value)*100
`
    },
    {
      id: 'eci-scores',
      title: 'ECI Scores',
      content: `ECI Scores - notes

This note talks about what all processes have taken place to get the final results in this section of the data. 

DESCRIPTION:

This data tells us about the ECI scores and ranks of the different countries that India trades with for the 5 critical minerals (lithium, graphite, nickel, rare earth elements, cobalt) over the years (2020, 2021, 2022, 2023) based on the import data that has been collected. 

SOURCE:

UN Comtrade

Import and Export Data:
https://comtradeplus.un.org/TradeFlow

ECI Scores and Ranks:
https://atlas.hks.harvard.edu/rankings

PROCESS:

The ECI scores and ranks for the years 2020, 2021, 2022, 2023 were collected as data for 2024 was not available and the import/export data that has been collected starts from 2020 and hence the overlapping years' data was collected.
This data was then used to check the ECI rankings of the countries that India trades with 
using the data of the import dependency and the ECI scores of the different countries over the years, the vulnerability scores were calculated 


CALCULATIONS DONE:
vulnerability score= Import Dependency Score  /  (ECI + 1)

`
    },
    {
      id: 'partner-risk',
      title: 'Partner Risk Scores',
      content: `Partner Risk Scores - notes

This note talks about what all processes have taken place to get the final results in this section of the data. 

DESCRIPTION:

This data tells us about the Partner Risk Scores of the different countries that India trades with for the 5 critical minerals (lithium, graphite, nickel, rare earth elements, cobalt) over the years (2020, 2021, 2022, 2023) based on the import data that has been collected. 

SOURCE:

UN Comtrade

Import and Export Data:
https://comtradeplus.un.org/TradeFlow

FSI Scores:
https://fragilestatesindex.org/excel/

WGI Scores:
https://databank.worldbank.org/source/worldwide-governance-indicators


PROCESS:

The FSI and WGI scores and ranks for the years 2020, 2021, 2022, 2023 were collected as data for 2024 was not available and the import/export data that has been collected starts from 2020 and hence the overlapping years' data was collected.
The data was cleaned by removing the years of data that was not required and different calculations was run on these two sets of data separately 
The FSI data gives an overall score based on 12 different factors on a scale of 0 to 120, this score was then normalised to a scale of 1 to 5 
the WGI data gives a score for 6 different indicators per country on a scale of -2.5 to 2,5, the average score of the 6 different indicators was normalised to a scale of 1 to 5 
These two normalised scores were taken and combined to give a score out of 5 that gave a whole view of the partner risk of different countries 
this score was then standardised to run in the same direction as the other data that has been collected, so that 1 is low risk and 5 is high risk 
this data was then attached to the import dependency scores sheet to find out the partner risk scores of the countries that India trades with 

CALCULATIONS DONE:

normalised_fsi = (1- fsi_score/120)*4+1 
avg_estimate_wgi = average of the 6 indicators per country: (cc + ge + pv + rl + rq + va)/6
avg_normalised_wgi = (wgi_score +2.5)*4+1
avg_fsi_wgi = (normalised_fsi + avg_normalised_wgi)/2
geo_fsi_wgi = squareroot(normalised_fsi * avg_normalised_wgi)
scaled score final = 6 - avg_fsi_wgi

`
    },
    
    
    
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Methodology
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          This section explains how import dependency, trade exposure, political risk, concentration risk, and composite risk scores were calculated across critical minerals. The approach aligns with widely used frameworks from the <b>EU Critical Raw Materials Act, USGS, DOE, and OECD supply-chain vulnerability models</b>.
          </p>
        </div>

        {/* Collapsible Panels */}
              <div className="space-y-4">
          {methodologySections.map((section) => (
            <div
              key={section.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => togglePanel(section.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
              >
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {section.title}
                  </h2>
                        </div>
                {openPanels[section.id] ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                )}
              </button>
              
              {openPanels[section.id] && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white">
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    {formatContent(section.content)}
                        </div>
                      </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Methodology;
